import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import "../../_css/Pdf.css";
import { getAllPdfs, getUserProfile, uploadPdf, retryEvaluation } from "../../requests";
import {
  deleteAuthToken,
  notifyError,
  notifySuccess,
  updateUserSessionData,
} from "../../utils/helper";
import { useNavigate } from "react-router-dom";
import { formatDateLong, formatGrade } from "../helper/formatters";
import { useDispatch } from "react-redux";
import { startLoader, stopLoader } from "../../_store/reducers/loaderReducer";
import SearchableDropdown from "../common/SearchableDropdown";
import { removeUserInformation } from "../../_store/reducers/userReducer";
import ValidationFindingsModal from "./ValidationFindingsModal";

const PdfUI = () => {
  // pagination
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(5);

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const answer_sheet_ref = useRef(null);
  const answer_key_ref = useRef(null);
  const question_paper_ref = useRef(null);

  const [highlight, setHighlight] = useState(false);
  const [isNotificationVisible, setIsNotificationVisible] = useState(false);

  const [answerSheets, setAnswerSheets] = useState([]);
  const [answerKey, setAnswerKey] = useState(null);
  const [questionPaper, setQuestionPaper] = useState(null);
  const [grade, setGrade] = useState("");
  const [subject, setSubject] = useState("");
  const [examName, setExamName] = useState("");
  const [markingBehaviour, setMarkingBehaviour] = useState("")
  const [totalCount, setTotalCount] = useState(0);

  const [pdfs, setAllPdfs] = useState([]);
  
  const [showValidationModal, setShowValidationModal] = useState(false);
  const [selectedAnswerSheetId, setSelectedAnswerSheetId] = useState(null);
  const [retryingId, setRetryingId] = useState(null);

  const handleRetryEvaluation = async (paperId, e) => {
    e?.stopPropagation?.();
    if (!paperId || retryingId) return;

    setRetryingId(paperId);
    dispatch(startLoader());
    try {
      const res = await retryEvaluation(paperId);
      if (res.status < 400) {
        notifySuccess(
          res.data?.message ||
            "Retry started. Status will show as Processing again."
        );
        await get_pdf();
      }
    } catch (error) {
      notifyError(
        error?.response?.data?.error ||
          "Could not retry this evaluation. Please try again."
      );
    } finally {
      setRetryingId(null);
      dispatch(stopLoader());
    }
  };

  const handleDivClick = (i) => {
    if (i == 0) {
      answer_sheet_ref.current.click();
      return;
    }
    if (i == 1) {
      answer_key_ref.current.click();
      return;
    }
    if (i == 2) {
      question_paper_ref.current.click();
      return;
    }
  };

  const handleFileChange = (e, i) => {
    if (i == 0) {
      const files = Array.from(e.target.files);
      setAnswerSheets(files);
    }

    if (i == 1) {
      const file = e.target.files[0];
      setAnswerKey(file);
    }
    if (i == 2) {
      const file = e.target.files[0];
      setQuestionPaper(file);
    }

    console.log(
      answer_sheet_ref.current.files,
      answer_key_ref.current?.files[0]?.name,
      question_paper_ref.current?.files[0]?.name
    );
    console.log("hi");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validate()) {
      return;
    }

    const formData = new FormData();

    formData.append("answer_key", answerKey);

    answerSheets.forEach((file) => {
      formData.append("answer_sheets", file);
    });
    if (questionPaper) {
      formData.append("question_paper", questionPaper);
    }

    formData.append("grade", grade);

    formData.append("subject", subject);
    formData.append("exam_name", examName);
    formData.append("marking_behaviour", markingBehaviour)

    dispatch(startLoader());

    try {
      const res = await uploadPdf(formData);
      const res2 = await getUserProfile();
            
      // Update sessionStorage and Redux using centralized function
      updateUserSessionData(res2?.data, dispatch);

      if (res.status < 400) {
        notifySuccess("Uploaded Successfully");
        setAnswerSheets([]);
        setAnswerKey(null);
        setQuestionPaper(null);
        setGrade("");
        setSubject("");
        setExamName("");
        setMarkingBehaviour("")

        question_paper_ref.current.value = null;
        answer_key_ref.current.value = null;
        answer_sheet_ref.current.value = null;

        get_pdf();

        // play notification sound
        const notificationSound = new Audio("/UploadSuccess.wav");
        notificationSound.play();
      }
    } catch (error) {
      console.log(error);

      if (error?.response?.status == 403) {
        notifyError(
          error?.response?.data?.error || "Session Time out log in again"
        );
        deleteAuthToken();
        navigate("/login");
        return;
      }
      notifyError(error.response?.data?.error || "Error Uploading files");
      // console.log(error);
    } finally {
      dispatch(stopLoader());
    }
  };

  const get_pdf = async () => {
    dispatch(startLoader());

    try {
      const res = await getAllPdfs({ page: page, limit: limit });
      // console.log(res.data.data);
      // Keep unique file_name only

      // const unique = [
      //   ...new Map(res.data.data.map((item) => [item.file_name, item])).values(),
      // ];

      // filter based on date desc
      const filteredData = res.data.uniqueData.sort(
        (a, b) => new Date(b.created_at) - new Date(a.created_at)
      );

      setAllPdfs(filteredData);
      setTotalCount(res.data.uniqueCount);
    } catch (error) {
      if (error?.response?.status == 403) {
        deleteAuthToken();

        notifyError(
          error?.response?.data?.error ||
            "Your session has expired. Please log in again."
        );

        navigate("/login");

        return;
      }
      notifyError(error.response?.data?.error || "Error fetching files");
    } finally {
      dispatch(stopLoader());
    }
  };

  const notify_visibility = (timer) => {
    if (isNotificationVisible) return;

    setIsNotificationVisible(true);

    setTimeout(() => {
      setIsNotificationVisible(false);
    }, timer);
  };
  const validate = () => {
    if (isNotificationVisible) return;

    notify_visibility(2000);

    const maxSize = 100 * 1024 * 1024; // 100MB in bytes

    if (!grade && !subject && !examName && !markingBehaviour) {
      notifyError("Please select class, grade, exam & marking behaviour");
      return false;
    }

    if (!grade) {
      notifyError("Please select a class");
      return false;
    }
    if (!subject) {
      notifyError("Please select a subject");
      return false;
    }
    if (!examName) {
      notifyError("Please enter an exam name");
      return false;
    }
    if(!markingBehaviour){
      notifyError("Please enter Marking Behaviour")
      return false;
    }

    if (answerSheets.length == 0) {
      notifyError("Please upload answer sheet to continue.");
      return false;
    }

    if (!answerKey) {
      notifyError("Please upload answer key to continue..");
      return false;
    }

    if (!questionPaper) {
      notifyError("Please upload question paper to continue..");
      return false;
    }

    if (answerSheets.length > 0) {
      for (const file of answerSheets) {
        if (file.type !== "application/pdf") {
          notifyError(
            `"${file.name}" is not a PDF file. try again with pdfs only`
          );
          return false;
        }

        if (file.size > maxSize) {
          notifyError(`"${file.name}" exceeds 100MB size limit`);
          return false;
        }
      }
    }

    if (answerKey) {
      if (answerKey.size > maxSize) {
        notifyError(`Answer Key exceeds 100MB size limit`);
        return false;
      }
    }

    if (questionPaper) {
      if (questionPaper.size > maxSize) {
        notifyError(`Question Paper exceeds 100MB size limit`);
        return false;
      }
    }

    return true;
  };

  // DRAG AND DROP
  // const handleFile = (file) => {
  //   if (file.type !== "application/pdf") {
  //     alert("Only PDF files are allowed");
  //     return;
  //   }
  //   console.log("Selected PDF:", file);
  //   // you can upload it to backend here
  // };

  const handleDrop = (e, id) => {
    e.preventDefault();
    setHighlight(false);
    if (id == 0) {
      const files = Array.from(e.dataTransfer.files);
      setAnswerSheets(files);
    }

    if (id == 1) {
      const file = e.dataTransfer.files[0];
      setAnswerKey(file);
    }
    if (id == 2) {
      const file = e.dataTransfer.files[0];
      setQuestionPaper(file);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setHighlight(true);
  };

  const handleDragLeave = () => {
    setHighlight(false);
  };

  const drp_classes = Array.from({ length: 12 }, (_, i) => i + 1);

  const drp_subjects = [
    "Mathematics",
    "English",
    "Science",
    "History",
    "Geography",
    "Physics",
    "Chemistry",
    "Biology",
    "Computer Science",
    "Information Technology",
    "Environmental Science",
    "Political Science",
    "Economics",
    "Business Studies",
    "Accountancy",
    "Sociology",
    "Psychology",
    "Philosophy",
    "Civics",
    "Literature",
    "Grammar",
    "Statistics",
    "Engineering Drawing (Theory)",
    "Design and Technology (Theory)",
    "Astronomy",
    "Geology",
    "Anthropology",
    "Archaeology",
    "Law",
    "Media Studies",
    "Communication Skills (Theory)",
    "Entrepreneurship",
    "Marketing",
    "Tourism and Hospitality (Theory)",
    "Fashion Studies (Theory)",
    "Agriculture (Theory)",
    "Computer Applications",
    "Applied Mathematics",
    "Modern History",
    "World History",
    "Ancient History",
    "Political Theory",
    "International Relations",
    "Public Administration",
    "Linguistics",
    "Environmental Studies",
    "Current Affairs",
    "General Knowledge",
    "Moral Science",
  ];

  const drp_exams = [
    "Unit Test",
    "Class Test",
    "Weekly Test",
    "Monthly Test",
    "Quarterly Exam",
    "Half-Yearly Exam",
    "Midterm Exam",
    "Final Exam",
    "Annual Exam",
    "Preliminary Exam",
    "Preparatory Exam",
    "Revision Test",
    "Surprise Test",
    "Class Assessment",
    "Formative Assessment",
    "Summative Assessment",
    "Open Book Test",
    "Closed Book Test",
    "Objective Test",
    "Subjective Test",
    "Multiple Choice Exam",
    "Descriptive Exam",
    "Short Answer Test",
    "Long Answer Test",
    "Written Exam",
    "Oral Exam",
    "Practical Exam",
    "Project Work Evaluation",
    "Assignment-based Test",
    "Lab Test",
    "Internal Assessment",
    "Progress Test",
    "Cycle Test",
    "Sectional Test",
    "Periodic Test",
    "Diagnostic Test",
    "Weekly Quiz",
    "Term Test",
    "Bi-monthly Test",
    "Semester Exam",
    "Class Participation Assessment",
    "Slip Test",
    "Spot Test",
    "Practice Exam",
    "Mock Exam",
    "Board Exam",
    "Scholarship Test",
    "Entrance Test (School Level)",
    "Achievement Test",
    "Competency Test",
  ];

  const mark_br = [
    "Strict",
    "Standard",
    "Lenient"
  ]

  function extractNames(obj) {
    const names = Object.values(obj)
      .map((item) => item.name)
      .filter(Boolean);

    if (names.length < 4) {
      return names;
    }

    return [...names.slice(0, 3), "..."];
  }

  //delete answer sheet
  const handleDelete = (id, name, index, el) => {
    el.stopPropagation();

    if (id == 1) {
      // Delete answer sheet at specific index
      const newAnswerSheets = answerSheets.filter((e, i) => i !== index);
      setAnswerSheets(newAnswerSheets);

      // Clear file input if no more answer sheets
      if (newAnswerSheets.length === 0 && answer_sheet_ref.current) {
        answer_sheet_ref.current.value = null;
      }
      return;
    }

    if (id == 2) {
      // Delete answer key
      setAnswerKey(null);
      if (answer_key_ref.current) {
        answer_key_ref.current.value = null;
      }
      return;
    }

    if (id == 3) {
      // Delete question paper
      setQuestionPaper(null);
      if (question_paper_ref.current) {
        question_paper_ref.current.value = null;
      }
      return;
    }
  };

  useEffect(() => {
    get_pdf();
  }, [page]);


  useEffect(() => {
    try {
      let user = localStorage.getItem('user');
      user = JSON.parse(user);
      if (user?.plan) {
        navigate('/our-plans', { state: { plan: user?.plan } })
        dispatch(removeUserInformation());
      }
    } catch (error) {
      return;
      
    }
  },[])

  return (
    <div className="pdf-main">
      <div className="pdf-upload">
        <div className="pdf-upload-heading">
          <div className="pdf-upload-heading-text">Upload Answer Sheets</div>
          <div className="pdf-upload-heading-subtext">
            Please provide necessary details
          </div>
        </div>
        <div className="pdf-upload-form">
          <div className="pdf-input-area">
            <label className="pdf-input-label">Class</label>
            <SearchableDropdown
              options={[...drp_classes]}
              selected={grade}
              setSelected={setGrade}
              type="number"
              allowManualEntry={false}
              placeHolder="Select class"
            />
          </div>
          <div className="pdf-input-area">
            <label className="pdf-input-label">Subject</label>
            <SearchableDropdown
              options={[...drp_subjects]}
              selected={subject}
              setSelected={setSubject}
              placeHolder="Select or type subject"
            />
          </div>
          <div className="pdf-input-area">
            <label className="pdf-input-label">Exam Name</label>

            <SearchableDropdown
              options={[...drp_exams]}
              selected={examName}
              setSelected={setExamName}
              placeHolder="Select or type exam"
            />

            {/* <option value="">-- Select Exam --</option>
              {drp_exams.map((e, i) => (
                <option key={i} value={e}>
                  {e}
                </option>
              ))} */}
          </div>
          <div className="pdf-input-area">
            <label className="pdf-input-label">Marking Behaviour</label>

            <SearchableDropdown
              options={[...mark_br]}
              selected={markingBehaviour}
              setSelected={setMarkingBehaviour}
              placeHolder="Select Marking Behaviour"
            />

            {/* <option value="">-- Select Exam --</option>
              {drp_exams.map((e, i) => (
                <option key={i} value={e}>
                  {e}
                </option>
              ))} */}
          </div>
        </div>
        <div className="pdf-upload-pdf">
          <div
            className="pdf-upload-add"
            onClick={() => handleDivClick(0)}
            onDrop={(e) => handleDrop(e, 0)}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
          >
            {answerSheets.length > 0 && (
              <div className="pdf-upload-add-text">
                {extractNames(answerSheets).map((e, i) => (
                  <div key={i}>
                    {e} - {" "}
                    <span
                      className="my-close-btn"
                     
                      onClick={(el) => handleDelete(1, e, i, el)}
                      onMouseEnter={(e) =>
                        (e.target.style.backgroundColor = "#FFB6C1")
                      }
                      onMouseLeave={(e) =>
                        (e.target.style.backgroundColor = "#FFE4E1")
                      }
                    >
                      ×
                    </span>
                  </div>
                ))}
              </div>
            )}
            {answerSheets.length == 0 && (
              <div className="pdf-upload-add-img">
                <img src="/upload-pdf.svg" width="100%" height="100%" />
              </div>
            )}
            {answerSheets.length == 0 && (
              <div className="pdf-upload-add-text">
                Drag & drop
                <span style={{ fontWeight: "700" }}>
                  {" "}
                  one or multiple Student Answer Sheet
                </span>{" "}
                PDF files here or click to browse
              </div>
            )}
          </div>
          <input
            type="file"
            multiple
            accept="application/pdf"
            onChange={(e) => handleFileChange(e, 0)}
            ref={answer_sheet_ref}
            style={{ display: "none" }}
          />
          <div
            className="pdf-upload-add"
            onClick={() => handleDivClick(1)}
            onDrop={(e) => handleDrop(e, 1)}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
          >
            {answerKey && (
              <div className="pdf-upload-add-text">
                {answerKey?.name} - {" "}
                <span
                  className="my-close-btn"
                  onClick={(el) => handleDelete(2, answerKey?.name, 0, el)}
                  onMouseEnter={(e) =>
                    (e.target.style.backgroundColor = "#FFB6C1")
                  }
                  onMouseLeave={(e) =>
                    (e.target.style.backgroundColor = "#FFE4E1")
                  }
                >
                  ×
                </span>
              </div>
            )}
            {!answerKey && (
              <div className="pdf-upload-add-img">
                <img src="/upload-pdf.svg" width="100%" height="100%" />
              </div>
            )}
            {!answerKey && (
              <div className="pdf-upload-add-text">
                Drag & drop{" "}
                <span style={{ fontWeight: "700" }}>Answer Key Memo </span> PDF
                files here or click to browse
              </div>
            )}
          </div>
          <input
            type="file"
            accept="application/pdf"
            onChange={(e) => handleFileChange(e, 1)}
            ref={answer_key_ref}
            style={{ display: "none" }}
          />
          <div
            className="pdf-upload-add"
            onClick={() => handleDivClick(2)}
            onDrop={(e) => handleDrop(e, 2)}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
          >
            {questionPaper && (
              <div className="pdf-upload-add-text">
                {questionPaper?.name} - {" "}
                <span
                 className="my-close-btn"
                  onClick={(el) => handleDelete(3, questionPaper?.name, 0, el)}
                  onMouseEnter={(e) =>
                    (e.target.style.backgroundColor = "#FFB6C1")
                  }
                  onMouseLeave={(e) =>
                    (e.target.style.backgroundColor = "#FFE4E1")
                  }
                >
                  ×
                </span>
              </div>
            )}
            {!questionPaper && (
              <div className="pdf-upload-add-img">
                <img src="/upload-pdf.svg" width="100%" height="100%" />
              </div>
            )}
            {!questionPaper && (
              <div className="pdf-upload-add-text">
                Drag & drop{" "}
                <span style={{ fontWeight: "700" }}>Question Paper</span> PDF
                files here or click to browse
              </div>
            )}
          </div>
          <input
            type="file"
            accept="application/pdf"
            onChange={(e) => handleFileChange(e, 2)}
            ref={question_paper_ref}
            style={{ display: "none" }}
          />
        </div>
        <div className="pdf-upload-submit" onClick={handleSubmit}>
          Continue
        </div>
      </div>

      <div className="pdf-list">
        <div className="pdf-list-heading">Upload History</div>
        <div className="pdf-table">
          <div className="pdf-table-head">
            <div className="pdf-head-cell" style={{ width: "16%" }}>
              File Name
            </div>
            <div className="pdf-head-cell">Subject</div>
            <div className="pdf-head-cell" style={{ width: "12%" }}>
              Class
            </div>
            <div className="pdf-head-cell">Exam name</div>
            <div className="pdf-head-cell" style={{ width: "15%" }}>
              Question paper
            </div>
            <div className="pdf-head-cell" style={{ width: "15%" }}>
              Uploaded On
            </div>
            <div className="pdf-head-cell" style={{ width: "12%" }}>
              Status
            </div>
          </div>
          {pdfs.length > 0 &&
            pdfs.map((e, i) => (
              <div
                key={i}
                className="pdf-table-row"
                onClick={() =>
                  e.status == 1 &&
                  navigate(
                    `/student-list/?filename=${encodeURIComponent(e.file_name)}`
                  )
                }
              >
                <div className="pdf-row-cell" style={{ width: "16%" }}>
                  {e.file_name}
                </div>
                <div className="pdf-row-cell">{e.subject}</div>
                <div className="pdf-row-cell" style={{ width: "12%" }}>
                  <span style={{ marginLeft: "12px" }}>
                    {formatGrade(e.grade)}
                  </span>
                </div>
                <div className="pdf-row-cell">{e.exam_name}</div>
                <div className="pdf-row-cell" style={{ width: "15%" }}>
                  <span style={{ marginLeft: "30px" }}>{"Yes"}</span>
                </div>
                <div className="pdf-row-cell" style={{ width: "15%" }}>
                  {formatDateLong(e.created_at)}
                </div>
                <div
                  className="pdf-row-cell"
                  style={{ width: "12%" }}
                >
                  {e.status == 3 ? (
                    <button
                      onClick={() => {
                        setSelectedAnswerSheetId(e.id);
                        setShowValidationModal(true);
                      }}
                      style={{
                        backgroundColor: "#FEF3C7",
                        color: "#92400E",
                        border: "1px solid #F59E0B",
                        padding: "6px 12px",
                        borderRadius: "6px",
                        fontSize: "13px",
                        fontWeight: "600",
                        cursor: "pointer",
                        transition: "all 0.2s",
                      }}
                      onMouseEnter={(ev) => {
                        ev.target.style.backgroundColor = "#FDE68A";
                      }}
                      onMouseLeave={(ev) => {
                        ev.target.style.backgroundColor = "#FEF3C7";
                      }}
                    >
                      Action Required
                    </button>
                  ) : e.status == 2 ? (
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "flex-start",
                        gap: "6px",
                      }}
                      onClick={(ev) => ev.stopPropagation()}
                    >
                      <span
                        style={{
                          color: "#FF7F7F",
                          fontWeight: 600,
                          fontSize: "13px",
                        }}
                      >
                        Failed
                      </span>
                      <button
                        type="button"
                        disabled={retryingId === e.id}
                        onClick={(ev) => handleRetryEvaluation(e.id, ev)}
                        style={{
                          backgroundColor: "#EFF6FF",
                          color: "#1D4ED8",
                          border: "1px solid #93C5FD",
                          padding: "5px 10px",
                          borderRadius: "6px",
                          fontSize: "12px",
                          fontWeight: "600",
                          cursor: retryingId === e.id ? "wait" : "pointer",
                          opacity: retryingId === e.id ? 0.7 : 1,
                        }}
                      >
                        {retryingId === e.id ? "Retrying…" : "Retry"}
                      </button>
                    </div>
                  ) : (
                    <div
                      style={{
                        color:
                          e.status == 1
                            ? "#0195c7"
                            : e.status == 0
                            ? "#0195c7"
                            : e.status == 4
                            ? "#6B7280"
                            : "#FF7F7F",
                        textDecoration: e.status == 1 ? "underline" : "none",
                        cursor: e.status == 1 ? "pointer" : "default",
                      }}
                      onClick={() => e.status == 1 && navigate(`/student-reports?id=${e.id}`)}
                    >
                      {e.status == 1
                        ? "Completed"
                        : e.status == 0
                        ? "Processing"
                        : e.status == 4
                        ? "Cancelled"
                        : "Failed"}
                    </div>
                  )}
                </div>
              </div>
            ))}
        </div>
        <div className="di-flex">
          <div style={{ color: "#6B7280" }}>
            {" "}
            Showing {(page - 1) * limit + 1} to{" "}
            {pdfs.length + (page - 1) * limit} out of {totalCount} items{" "}
          </div>
          <div
            className="di-flex"
            style={{ justifyContent: "end", gap: "10px" }}
          >
            <button
              disabled={page == 1}
              className="my-btn"
              style={{
                backgroundColor: "#E0F5FE",
                color: "#01A7DE",
                opacity: page == 1 ? 0.4 : 1,
              }}
              onClick={() => setPage((p) => (p > 1 ? p - 1 : p))}
            >
              Previous
            </button>
            <button
              disabled={pdfs.length + (page - 1) * limit >= totalCount}
              className="my-btn"
              style={{
                backgroundColor: "#E0F5FE",
                color: "#01A7DE",
                opacity:
                  pdfs.length + (page - 1) * limit >= totalCount ? 0.4 : 1,
              }}
              onClick={() => setPage((p) => (pdfs.length ? p + 1 : p))}
            >
              Next
            </button>
          </div>
        </div>
      </div>

      {/* Validation Findings Modal */}
      {showValidationModal && selectedAnswerSheetId && (
        <ValidationFindingsModal
          answersheetId={selectedAnswerSheetId}
          fn={() => {
            setShowValidationModal(false);
            setSelectedAnswerSheetId(null);
          }}
          onSuccess={() => {
            // Reload the list to reflect updated status
            get_pdf();
          }}
        />
      )}
    </div>
  );
};

export default PdfUI;
